import React, { useCallback } from 'react';
import { styled } from '@compiled/react';
import debounce from 'lodash/debounce';
import noop from 'lodash/noop';
import VisuallyHidden from '@atlaskit/visually-hidden';
import { SearchField } from '@atlassian/jira-searchfield/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import messages from '../../messages.tsx';

type Props = {
	inputRef?: {
		current: null | HTMLInputElement;
	};
	label: string;
	value: string;
	isDisabled?: boolean;
	onChange: (arg1: string) => void;
	onFocus?: () => void;
	onSendOptionAnalytics?: (arg1: { searchStringLength: number }) => void;
};

type DebounceOnChangeProps = {
	targetValue: string;
	onChange: (arg1: string) => void;
	onSendOptionAnalytics?: (props: { searchStringLength: number }) => void;
};

const accessibleDescriptionId = 'text-field-accessible-description-id';
const debounceOnChange = debounce(
	({ onSendOptionAnalytics = noop, onChange, targetValue }: DebounceOnChangeProps) => {
		onChange(targetValue);
		onSendOptionAnalytics({
			searchStringLength: targetValue ? targetValue.length : 0,
		});
	},
	500,
	{
		leading: false,
		trailing: true,
	},
);

const TextField = ({
	inputRef,
	label,
	value,
	isDisabled = false,
	onChange,
	onFocus,
	onSendOptionAnalytics,
}: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const handleChange = useCallback(
		(targetValue: string) => {
			debounceOnChange({
				onChange,
				targetValue,
				onSendOptionAnalytics,
			});
		},
		[onChange, onSendOptionAnalytics],
	);

	return (
		<StyledContainer>
			<VisuallyHidden id={accessibleDescriptionId}>
				{formatMessage(messages.accessibleDescription)}
			</VisuallyHidden>
			<SearchField
				onChange={handleChange}
				onFocus={onFocus}
				value={value}
				placeholder={label}
				placeholderAlwaysVisible
				isCompact
				isResponsive
				inputRef={inputRef}
				testId="jql-builder-basic.common.ui.text-field.search-field"
				isDisabled={isDisabled}
				showIconBeforeInput
				describedById={accessibleDescriptionId}
			/>
		</StyledContainer>
	);
};

export default TextField;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledContainer = styled.div({
	position: 'relative',
});
